/**
 * BlogHeader — shared top navigation for every /blog/* page.
 *
 * Extracted from BlogHome.jsx so BlogTopicHub, BlogQuestions and
 * BlogSubpages render the same 7-tab nav (News, Explore Places,
 * Explore Topics ▾, Questions, Publications, Writers, About) instead
 * of their previous slim 3-tab strip.
 *
 * Props:
 *   activeNav  optional slug matching a tab's label (kebab-cased)
 *              e.g. "questions" or "explore-topics" → paints that tab
 *              in the teal accent + bold.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ChevronDown, Search, Globe, Loader2 } from "lucide-react";
import LocationSuggestBanner from "@/components/LocationSuggestBanner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/** Fire browser geolocation → snap to seeded city → save to sp_home_city.
 *  Used by the "Set your city" chip so first-time mobile readers can
 *  auto-clear the "Location not set" state with one tap. */
async function autoLocateAndSaveCity() {
  if (!("geolocation" in navigator)) {
    throw new Error("geolocation_unsupported");
  }
  const pos = await new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 300_000,
    });
  });
  const { latitude, longitude } = pos.coords;
  const r = await fetch(
    `${API}/publisher/v1/places/nearest?lat=${latitude}&lng=${longitude}`,
  );
  if (!r.ok) throw new Error(`nearest_${r.status}`);
  const d = await r.json();
  const city = {
    city:         d.name,
    state:        d.state,
    country:      d.country,
    country_code: d.country_code,
    slug:         d.slug,
    latitude,
    longitude,
    source:       d.source === "db" ? "gps-db" : "gps-ext",
    distance_km:  d.distance_km,
  };
  const payload = JSON.stringify(city);
  window.localStorage.setItem("sp_home_city", payload);
  if (!window.localStorage.getItem("np_home_city")) {
    window.localStorage.setItem("np_home_city", payload);
  }
  try {
    window.dispatchEvent(new CustomEvent("sp:home-city-changed", { detail: city }));
  } catch { /* noop */ }
  return city;
}

const TOPIC_OPTIONS = [
  { slug: "health",             label: "Health" },
  { slug: "education",          label: "Education" },
  { slug: "finance",            label: "Finance" },
  { slug: "food",               label: "Food" },
  { slug: "travel",             label: "Travel" },
  { slug: "nature",             label: "Nature" },
  { slug: "science",            label: "Science" },
  { slug: "weather",            label: "Weather" },
  { slug: "farmers",            label: "Farmers" },
  { slug: "alumni",             label: "Alumni" },
  { slug: "family-trees",       label: "Family Trees" },
  { slug: "memorials",          label: "Memorials" },
  { slug: "homes-real-estate",  label: "Homes & Real Estate" },
  { slug: "family-heritage",    label: "Family & Heritage" },
  { slug: "community",          label: "Community" },
  { slug: "local-news",         label: "Local News" },
  { slug: "arts-culture",       label: "Arts & Culture" },
  { slug: "history",            label: "History" },
  { slug: "faith-spirituality", label: "Faith & Spirituality" },
  { slug: "technology",         label: "Technology" },
  { slug: "business",           label: "Business" },
];

const NAV = [
  { label: "News",           to: "/blog/news",     slug: "news" },
  { label: "Explore Places", to: "/blog/places",   slug: "explore-places" },
  { label: "Explore Topics", to: "/blog/topics",   slug: "explore-topics", hasMenu: "topics" },
  { label: "Questions",      to: "/blog/questions", slug: "questions" },
  { label: "Publications",   to: "/blog/publications", slug: "publications" },
  { label: "Writers",        to: "/blog/writers",  slug: "writers" },
  { label: "About",          to: "/blog/about-socialpulse-blog", slug: "about" },
];

export default function BlogHeader({ activeNav = "" }) {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [homeCity, setHomeCity] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState(null);

  useEffect(() => {
    try {
      const raw =
        window.localStorage.getItem("sp_home_city") ||
        window.localStorage.getItem("np_home_city");
      if (raw) setHomeCity(JSON.parse(raw));
    } catch { /* noop */ }
  }, []);

  // "Set your city ›" tap: first try GPS auto-locate; fall back to
  // manual picker on denial or failure.
  const handleSetCity = useCallback(async () => {
    if (homeCity) {
      navigate("/local");
      return;
    }
    if (!("geolocation" in navigator)) {
      navigate("/local");
      return;
    }
    setLocating(true);
    setLocateError(null);
    try {
      const city = await autoLocateAndSaveCity();
      setHomeCity(city);
    } catch (e) {
      // Permission denied or nearest lookup failed → hand off to manual picker
      const msg = (e && e.message) || "";
      if (msg.includes("PERMISSION") || (e && e.code === 1)) {
        setLocateError("Location permission denied — pick manually.");
      } else {
        setLocateError("Couldn't detect your location — pick manually.");
      }
      setTimeout(() => navigate("/local"), 900);
    } finally {
      setLocating(false);
    }
  }, [homeCity, navigate]);

  return (
    <header
      className="border-b sticky top-0 z-30"
      style={{ borderColor: "rgba(15,118,110,0.08)", background: "rgba(250,250,247,0.94)", backdropFilter: "blur(10px)" }}
      data-testid="blog-header"
    >
      {/* Slim location strip */}
      <div
        className="max-w-[1400px] mx-auto px-6 md:px-10 py-1.5 flex items-center gap-2 text-xs text-slate-600 border-b"
        style={{ borderColor: "rgba(15,23,42,0.05)" }}
        data-testid="blog-location-strip"
      >
        <MapPin className="w-3 h-3" style={{ color: "#f59e0b" }} />
        <span>
          {homeCity ? (
            <>
              You&apos;re reading from{" "}
              <strong className="text-slate-900">
                {homeCity.city || homeCity.name}
                {homeCity.state ? `, ${homeCity.state}` : ""}
                {homeCity.country ? ` · ${homeCity.country}` : ""}
              </strong>
            </>
          ) : (
            <span className="text-slate-500">Location not set —</span>
          )}
        </span>
        <button
          onClick={handleSetCity}
          disabled={locating}
          className="font-semibold hover:underline inline-flex items-center gap-1 disabled:opacity-70"
          style={{ color: "#0F766E" }}
          data-testid="blog-location-change"
        >
          {locating && <Loader2 className="w-3 h-3 animate-spin" />}
          {locating
            ? "Detecting…"
            : homeCity
              ? "Change ›"
              : "Set your city ›"}
        </button>
        {locateError && (
          <span className="text-[11px] text-amber-700" data-testid="blog-location-error">
            {locateError}
          </span>
        )}
      </div>

      <LocationSuggestBanner
        hasHomeCity={!!homeCity}
        onAccepted={(city) => setHomeCity(city)}
        onChange={() => navigate("/local")}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-3 md:py-4 flex items-center gap-3 md:gap-6">
        <a href="/blog" className="flex flex-col items-center md:flex-row md:items-center gap-1 md:gap-3 shrink-0" data-testid="blog-logo">
          <span
            className="w-10 h-10 rounded-full grid place-items-center"
            style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}
            aria-hidden="true"
          >
            <Globe className="w-5 h-5 text-white" />
          </span>
          <span className="md:hidden text-[10px] font-semibold text-slate-600 tracking-wide">Home</span>
          <span
            className="hidden md:inline text-xl"
            style={{ fontFamily: "'Lora', serif", fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            SocialPulse<sup className="text-xs" style={{ color: "#0F766E" }}>®</sup>{" "}
            <span style={{ color: "#0F766E" }}>Blog</span>
          </span>
        </a>

        {/* Mobile search input — visible only < md */}
        <label className="md:hidden flex-1 flex items-center gap-2 rounded-full bg-white border px-3 py-2" style={{ borderColor: "rgba(15,23,42,0.08)" }}>
          <Search className="w-4 h-4 shrink-0" style={{ color: "#0F766E" }} />
          <input
            type="search"
            placeholder="Search stories, places…"
            className="w-full bg-transparent outline-none text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.currentTarget.value.trim()) {
                navigate(`/blog/search?q=${encodeURIComponent(e.currentTarget.value.trim())}`);
              }
            }}
            data-testid="blog-search-mobile"
          />
        </label>

        <nav className="hidden md:flex items-center gap-5 flex-1" data-testid="blog-nav">
          {NAV.map((n) => {
            const isActive = activeNav === n.slug;
            return (
              <div
                key={n.to}
                className="relative"
                onMouseEnter={() => n.hasMenu && setOpenMenu(n.hasMenu)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  onClick={() => navigate(n.to)}
                  className={`text-[15px] transition-colors ${
                    isActive ? "font-semibold" : ""
                  } text-slate-700 hover:text-slate-950`}
                  style={isActive ? { color: "#0F766E" } : {}}
                  data-testid={`blog-nav-${n.slug}`}
                >
                  {n.label}
                  {n.hasMenu ? <ChevronDown className="w-3.5 h-3.5 inline ml-0.5" /> : null}
                </button>
                {n.hasMenu === "topics" && openMenu === "topics" && (
                  <div
                    className="absolute top-full left-0 mt-1 w-64 bg-white rounded-2xl shadow-2xl p-2 z-40 border max-h-[420px] overflow-y-auto"
                    style={{ borderColor: "rgba(15,23,42,0.08)" }}
                    data-testid="blog-topics-menu"
                  >
                    {TOPIC_OPTIONS.map((t) => (
                      <button
                        key={t.slug}
                        onClick={() => navigate(`/blog/topics/${t.slug}`)}
                        className="block w-full text-left px-3 py-1.5 rounded-lg text-sm hover:bg-slate-100"
                        data-testid={`blog-topic-menu-${t.slug}`}
                      >
                        {t.label}
                      </button>
                    ))}
                    <div className="border-t my-1" style={{ borderColor: "rgba(15,23,42,0.05)" }} />
                    <button
                      onClick={() => navigate("/blog/topics/suggest")}
                      className="block w-full text-left px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-slate-100"
                      style={{ color: "#0F766E" }}
                      data-testid="blog-topic-menu-other"
                    >
                      Other — suggest a topic ›
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 md:gap-5 ml-auto">
          <button
            onClick={() => navigate("/blog/search")}
            className="hidden md:grid w-9 h-9 rounded-full place-items-center hover:bg-slate-100"
            aria-label="Search"
            data-testid="blog-search-btn"
          >
            <Search className="w-5 h-5" style={{ color: "#0F766E" }} />
          </button>
          <button
            onClick={() => navigate("/auth")}
            className="text-[15px] text-slate-700 hover:text-slate-950 hidden sm:inline"
            data-testid="blog-signin"
          >
            Sign In
          </button>
          <button
            onClick={() => navigate("/publisher/write")}
            className="rounded-full px-3 md:px-5 py-2 md:py-2.5 text-[12px] md:text-[14px] font-semibold text-white transition-transform hover:scale-[1.02] whitespace-nowrap"
            style={{ background: "#0F766E" }}
            data-testid="blog-start-writing"
          >
            Start Writing
          </button>
        </div>
      </div>

      {/* Mobile category strip — horizontal scroll of tabs (< md only) */}
      <nav
        className="md:hidden flex items-center gap-1.5 px-4 pb-2.5 overflow-x-auto no-scrollbar"
        style={{ scrollbarWidth: "none" }}
        data-testid="blog-nav-mobile"
      >
        {NAV.map((n) => {
          const isActive = activeNav === n.slug;
          return (
            <button
              key={n.to}
              onClick={() => navigate(n.to)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap border transition-colors ${
                isActive
                  ? "text-white"
                  : "text-slate-700 bg-white hover:text-slate-950"
              }`}
              style={
                isActive
                  ? { background: "#0F766E", borderColor: "#0F766E" }
                  : { borderColor: "rgba(15,23,42,0.08)" }
              }
              data-testid={`blog-nav-mobile-${n.slug}`}
            >
              {n.label}
            </button>
          );
        })}
      </nav>
    </header>
  );
}
